import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { saveImage } from '../lib/saveImage.js';

interface SaveSheetProps {
  url: string;
  filename: string;
  onClose: () => void;
}

/**
 * SaveSheet — the mobile "save to gallery" path.
 *
 * iOS gives the web no direct write to the photo library, and both of its
 * indirect routes have failure modes:
 *   - The share sheet hides "Save Image" for PNG files (WebKit regression);
 *     we work around it by re-encoding to JPEG before sharing.
 *   - The long-press menu's "Add to Photos" silently no-ops inside in-app
 *     browsers (X, wallets) whose host app lacks photo permission.
 * So this sheet leads with the JPEG share (primary) and keeps long-press as
 * the alternate, with a one-line note for the in-app-browser case.
 */
export function SaveSheet({ url, filename, onClose }: SaveSheetProps) {
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const share = async (): Promise<void> => {
    setSharing(true);
    try {
      const outcome = await saveImage(url, filename);
      if (outcome === 'shared' || outcome === 'downloaded') setShared(true);
    } finally {
      setSharing(false);
    }
  };

  return createPortal(
    <div
      className="backdrop-enter fixed inset-0 z-50 flex flex-col items-center justify-center gap-5 px-5 py-8"
      style={{ background: 'rgba(6,5,8,0.96)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Save image"
      onClick={onClose}
    >
      <img
        src={url}
        alt="Your gilding, ready to save"
        className="sheet-enter max-h-[56vh] w-auto max-w-full rounded-xs"
        style={{
          WebkitTouchCallout: 'default',
          border: '1px solid rgb(var(--color-hairline-strong))',
        }}
        onClick={(e) => e.stopPropagation()}
      />

      <div
        className="flex w-full max-w-xs flex-col items-stretch gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="btn-primary"
          disabled={sharing}
          onClick={() => void share()}
        >
          {sharing ? 'Opening…' : shared ? 'Open share sheet again' : 'Save to Photos'}
        </button>
        <p className="text-center font-display italic text-caption text-ink-soft">
          Choose <span className="not-italic text-accent">Save Image</span> on the
          sheet that opens. Or press and hold the picture above, then{' '}
          <span className="not-italic text-accent">Add to Photos</span>.
        </p>
        <p className="text-center font-body text-[11px] leading-relaxed text-ink-subtle">
          Inside another app&apos;s browser, saving needs that app&apos;s photo
          permission. If nothing lands in your gallery, open this page in Safari
          or Chrome and try again.
        </p>
        <button type="button" className="btn-ghost" onClick={onClose}>
          Done
        </button>
      </div>
    </div>,
    document.body,
  );
}

/** True on touch-first devices, where the save viewer is the save path. */
export function isCoarsePointer(): boolean {
  return window.matchMedia('(pointer: coarse)').matches;
}
